import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Mail, ArrowRight } from "lucide-react";
import { portalApi, portalToken, formatApiError } from "./api";
import { BRAND } from "@/data/brand";

export default function PortalLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { token, user } = await portalApi.login(email, password);
      portalToken.set(token);
      toast.success("Signed in");
      nav((user.role === "admin" || user.role === "super_admin") ? "/digital-card/admin" : "/digital-card");
    } catch (err) {
      toast.error(formatApiError(err) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      data-testid="portal-login-page"
      className="min-h-screen flex flex-col justify-center px-6 py-12 bg-midnight-deeper relative overflow-hidden"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse at 50% 20%, rgba(0,119,255,0.15), transparent 60%)" }}
        aria-hidden
      />
      <div className="relative max-w-md w-full mx-auto">
        <Link to="/" className="inline-flex items-center gap-3 mb-14 group">
          <img src={BRAND.logoWhite} alt="AG Public Adjusting" className="h-10 w-auto object-contain" />
          <div className="leading-tight">
            <div className="font-mono text-[0.55rem] mt-0.5 text-gold/80 tracking-[0.28em]">DIGITAL CARD PORTAL</div>
          </div>
        </Link>

        <h1 className="font-display-tight text-white text-[clamp(2rem,7vw,3rem)] tracking-[-0.04em] leading-[1] mb-4">
          Welcome.
        </h1>
        <p className="text-ag-text-secondary text-[0.95rem] leading-[1.6] font-light mb-10 max-w-sm">
          Create and manage your official AG Public Adjusting digital business card.
        </p>

        <form onSubmit={submit} className="glass-strong p-6 sm:p-8 space-y-6" data-testid="portal-login-form">
          <label className="block">
            <span className="font-mono text-[0.6rem] text-ag-text-muted tracking-[0.28em] block mb-1 uppercase">Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="ag-input"
              data-testid="portal-login-email"
            />
          </label>
          <label className="block">
            <span className="font-mono text-[0.6rem] text-ag-text-muted tracking-[0.28em] block mb-1 uppercase">Password</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="ag-input"
              data-testid="portal-login-password"
            />
          </label>
          <button
            type="submit"
            disabled={loading}
            className="btn-gold w-full inline-flex items-center justify-center gap-2 !py-3.5 disabled:opacity-50"
            data-testid="portal-login-submit"
          >
            {loading ? "Signing in…" : "Sign In"}
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
          <p className="text-center text-ag-text-muted text-xs font-light">
            Forgot your password? <a href={BRAND.emailHref} className="text-gold hover:underline">Contact your admin</a>
          </p>
        </form>

        <div className="mt-10 flex items-center justify-center gap-6 text-xs text-ag-text-muted font-mono tracking-[0.22em] uppercase">
          <a href={BRAND.phoneHref} className="inline-flex items-center gap-2 hover:text-gold transition-colors">
            <Phone className="w-3 h-3" /> {BRAND.phoneDisplay}
          </a>
          <span className="w-px h-3 bg-white/10" />
          <a href={BRAND.emailHref} className="inline-flex items-center gap-2 hover:text-gold transition-colors">
            <Mail className="w-3 h-3" /> Support
          </a>
        </div>
      </div>
    </main>
  );
}
